
import React, {
  useState,
  useEffect,
} from "react"

import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"

import PulseLoader from "react-spinners/PulseLoader"

import {
  DocumentTextIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline"

import {
  useGetUserSchedulingPagesQuery,
  useUpdateUserSchedulingPageMutation,
} from "@/features/userSchedulingPages/userSchedulingPagesApiSlice"

import {
  useGetOrgIntentionBoardsQuery,
} from "@/features/orgIntentionBoards/orgIntentionBoardsApiSlice"

import EtUserSchedulingSlotManager
  from "./EtUserSchedulingSlotManager"

import useAuth from "@/hooks/useAuth"

import {
  useGetGoogleCalendarConnectionStatusQuery,
} from "./googleCalendarAuthApiSlice"


/*
|--------------------------------------------------------------------------
| Default Timezones
|--------------------------------------------------------------------------
*/

const defaultTimezones = [

  { label: "US Pacific", timezone: "America/Los_Angeles" },
  { label: "US Mountain", timezone: "America/Denver" },
  { label: "US Central", timezone: "America/Chicago" },
  { label: "US Eastern", timezone: "America/New_York" },
  { label: "UK", timezone: "Europe/London" },
  { label: "India", timezone: "Asia/Kolkata" },

]


/*
|--------------------------------------------------------------------------
| Ensure Mountain Timezone
|--------------------------------------------------------------------------
*/

const ensureMountainTimezone = timezones => {

  const baseTimezones =
    timezones?.length
      ? timezones
      : defaultTimezones

  const hasMountain =
    baseTimezones.some(
      item =>
        item.timezone === "America/Denver"
    )

  if (hasMountain) {
    return baseTimezones
  }

  const pacificIndex =
    baseTimezones.findIndex(
      item =>
        item.timezone === "America/Los_Angeles"
    )

  const mountainTimezone = {
    label: "US Mountain",
    timezone: "America/Denver",
  }

  if (pacificIndex >= 0) {

    return [
      ...baseTimezones.slice(0, pacificIndex + 1),
      mountainTimezone,
      ...baseTimezones.slice(pacificIndex + 1),
    ]

  }

  return [
    mountainTimezone,
    ...baseTimezones,
  ]

}


/*
|--------------------------------------------------------------------------
| Initial Form State
|--------------------------------------------------------------------------
*/

const buildInitialFormState = page => ({

  intentionBoardId:
    page?.intentionBoardId?._id ||
    page?.intentionBoardId ||
    "",

  communicationType:
    page?.communicationType || "",

  pageName:
    page?.pageName || "",

  publicSlug:
    page?.publicSlug || "",

  passcode:
    "",

  pageDescription:
    page?.pageDescription || "",

  googleCalendarId:
    page?.googleCalendarId || "primary",

  allowedTimezones:
    ensureMountainTimezone(
      page?.allowedTimezones
    ),

  isActive:
    page?.isActive ?? true,

})


/*
|--------------------------------------------------------------------------
| Outer Component
|--------------------------------------------------------------------------
*/

const EtUpdateUserSchedulingPage = ({
  id,
  setActiveAdminView,
}) => {

  // First Created - 06 May 2026
  // Author - Atul Pandey
  // Purpose - To update a Trinity SlotBoard scheduling page.
  // Updated - 09 May 2026 - Added scheduling slot manager
  // Updated - 16 May 2026 - Google Calendar integration
  // Updated - 18 May 2026 - Added US Mountain timezone
  // Updated - 27 Sep 2026 - Intention Board and communication type
  // Updated - 27 Sep 2026 - Page Details / Slots tabs
  // Updated - 27 Sep 2026 - Common footer and scrollable slot area

  useTitle("Update SlotBoard Page")

  const {
    userSchedulingPage,
  } = useGetUserSchedulingPagesQuery(
    undefined,
    {
      selectFromResult: ({ data }) => ({
        userSchedulingPage:
          data?.entities?.[id],
      }),
    }
  )

  if (!userSchedulingPage) {

    return (
      <div className="p-4">
        <PulseLoader color="#ea580c" size={5} />
      </div>
    )

  }

  return (

    <EtUpdateUserSchedulingPageForm
      key={id}
      userSchedulingPage={userSchedulingPage}
      id={id}
      setActiveAdminView={setActiveAdminView}
    />

  )

}


/*
|--------------------------------------------------------------------------
| Page Form
|--------------------------------------------------------------------------
*/

const EtUpdateUserSchedulingPageForm = ({
  userSchedulingPage,
  id,
  setActiveAdminView,
}) => {

  const navigate = useNavigate()

  const {
    token,
  } = useAuth()

  const [
    activeTab,
    setActiveTab,
  ] = useState("details")

  const [
    formData,
    setFormData,
  ] = useState(
    () =>
      buildInitialFormState(
        userSchedulingPage
      )
  )


  /*
  |--------------------------------------------------------------------------
  | Update Mutation
  |--------------------------------------------------------------------------
  */

  const [
    updateUserSchedulingPage,
    {
      isLoading,
      isSuccess,
      isError,
      error,
    },
  ] = useUpdateUserSchedulingPageMutation()


  /*
  |--------------------------------------------------------------------------
  | Intention Boards
  |--------------------------------------------------------------------------
  */

  const {
    data: intentionBoardsData,
    isLoading: isLoadingIntentionBoards,
    isError: isIntentionBoardsError,
    error: intentionBoardsError,
  } = useGetOrgIntentionBoardsQuery({})

  const intentionBoards =
    intentionBoardsData?.ids
      ?.map(
        boardId =>
          intentionBoardsData?.entities?.[boardId]
      )
      ?.filter(Boolean) || []


  /*
  |--------------------------------------------------------------------------
  | Current Board
  |--------------------------------------------------------------------------
  |
  | Preserve visibility of the page's existing association, including
  | historical boards that may no longer be active.
  |
  */

  const currentBoardId =
    String(
      userSchedulingPage?.intentionBoardId?._id ||
      userSchedulingPage?.intentionBoardId ||
      ""
    )

  const currentBoardInOptions =
    intentionBoards.some(
      board =>
        String(board._id) === currentBoardId
    )


  /*
  |--------------------------------------------------------------------------
  | Google Calendar
  |--------------------------------------------------------------------------
  */

  const {
    data: googleCalendarStatus,
    isLoading: isLoadingGoogleCalendarStatus,
  } =
    useGetGoogleCalendarConnectionStatusQuery()

  const googleCalendarConnected =
    googleCalendarStatus?.googleCalendarConnected || false

  const googleEmail =
    googleCalendarStatus?.googleEmail || ""


  /*
  |--------------------------------------------------------------------------
  | Successful Update
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!isSuccess) return

    if (setActiveAdminView) {
      setActiveAdminView("userSchedulingPages")
    } else {
      navigate("/userSchedulingPages")
    }

  }, [
    isSuccess,
    navigate,
    setActiveAdminView,
  ])


  /*
  |--------------------------------------------------------------------------
  | Form Changes
  |--------------------------------------------------------------------------
  */

  const handleChange = e => {

    const {
      name,
      value,
      type,
      checked,
    } = e.target

    setFormData(prev => ({

      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,

    }))

  }


  const handleTimezoneChange = (
    index,
    key,
    value
  ) => {

    setFormData(prev => ({

      ...prev,

      allowedTimezones:
        prev.allowedTimezones.map(
          (item, i) =>
            i === index
              ? {
                  ...item,
                  [key]: value,
                }
              : item
        ),

    }))

  }


  /*
  |--------------------------------------------------------------------------
  | Connect Google Calendar
  |--------------------------------------------------------------------------
  */

  const handleConnectGoogleCalendar = () => {

    if (!token) return

    window.location.href =
      `${process.env.REACT_APP_SERVER_URL}/googleCalendarAuth?token=${token}`

  }


  /*
  |--------------------------------------------------------------------------
  | Submit
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async e => {

    e?.preventDefault()

    const pageData = {

      schedulingPageId:
        id,

      intentionBoardId:
        formData.intentionBoardId || null,

      communicationType:
        formData.communicationType || null,

      pageName:
        formData.pageName.trim(),

      publicSlug:
        formData.publicSlug.trim().toLowerCase(),

      pageDescription:
        formData.pageDescription,

      googleCalendarId:
        formData.googleCalendarId,

      allowedTimezones:
        formData.allowedTimezones,

      isActive:
        formData.isActive,

    }

    if (formData.passcode) {

      pageData.passcode =
        formData.passcode

    }

    try {

      await updateUserSchedulingPage(
        pageData
      ).unwrap()

    } catch (error) {

      console.error(
        "SlotBoard update error:",
        error
      )

    }

  }


  const handleCancel = () => {

    if (setActiveAdminView) {
      setActiveAdminView("userSchedulingPages")
    } else {
      navigate("/userSchedulingPages")
    }

  }


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="bg-gray-200 p-1 mx-auto mt-3 rounded-lg shadow-md shadow-black font-poppins">

      <div
        className="
          bg-white
          rounded
          m-1
          p-3
          flex
          flex-col
          lg:h-[78vh]
          lg:min-h-[550px]
          min-w-0
        "
      >

        {/* Header */}

        <div className="shrink-0">

          <div className="flex flex-wrap justify-between items-start gap-2">

            <div>

              <p className="font-bold text-lg text-gray-800">
                Update SlotBoard Page
              </p>

              <p className="text-[10px] text-gray-500 mt-1">
                {userSchedulingPage.pageName || "Scheduling Page"}
              </p>

            </div>

            <span
              className={`text-[9px] font-bold rounded-full px-3 py-1 ${
                formData.isActive
                  ? "bg-lime-100 text-lime-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {formData.isActive ? "Active" : "Inactive"}
            </span>

          </div>


          {/* Tabs */}

          <div className="flex flex-wrap gap-2 mt-4 border-b border-gray-200 pb-2">

            <button
              type="button"
              onClick={() => setActiveTab("details")}
              className={`flex items-center gap-2 rounded px-4 py-2 text-[10px] font-bold ${
                activeTab === "details"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <DocumentTextIcon className="h-4 w-4" />
              Page Details
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("slots")}
              className={`flex items-center gap-2 rounded px-4 py-2 text-[10px] font-bold ${
                activeTab === "slots"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              <CalendarDaysIcon className="h-4 w-4" />
              Slots
            </button>

          </div>

        </div>


        {/* Error */}

        {isError && (

          <p className="shrink-0 text-xs text-red-700 bg-red-50 rounded p-2 mt-2">
            {error?.data?.message || "Unable to update SlotBoard."}
          </p>

        )}


        {/* Content Area */}

        <div
          className="
            flex-1
            min-h-0
            min-w-0
            overflow-hidden
            mt-2
          "
        >

          {/* Page Details Tab */}

          {activeTab === "details" && (

            <div className="h-full overflow-y-auto bg-gray-50 rounded p-3 text-xs space-y-3">

              {/* Intention Board */}

              <div className="bg-orange-50 border border-orange-100 rounded p-3">

                <label className="block text-[10px] font-bold text-orange-700 mb-1">
                  Intention Board
                </label>

                <select
                  name="intentionBoardId"
                  value={formData.intentionBoardId}
                  onChange={handleChange}
                  disabled={isLoadingIntentionBoards}
                  className="w-full rounded border border-orange-200 bg-white p-2 text-xs"
                >

                  <option value="">
                    Legacy Page – Not Associated
                  </option>

                  {currentBoardId &&
                    !currentBoardInOptions && (

                      <option value={currentBoardId}>
                        Current Intention Board
                      </option>

                    )}

                  {intentionBoards.map(board => (

                    <option
                      key={board._id}
                      value={board._id}
                    >
                      {board.boardName || "Intention Board"}
                    </option>

                  ))}

                </select>

                {isIntentionBoardsError && (
                  <p className="text-[10px] text-red-700 mt-2">
                    {intentionBoardsError?.data?.message ||
                      "Unable to load Intention Boards."}
                  </p>
                )}

                <p className="text-[9px] text-gray-500 mt-2">
                  Legacy pages can remain unassociated. Select an Intention Board to connect this page.
                </p>

              </div>


              {/* Communication Type */}

              <div className="bg-white rounded p-3">

                <label className="block text-[10px] font-bold text-gray-700 mb-1">
                  Communication Type (Optional)
                </label>

                <select
                  name="communicationType"
                  value={formData.communicationType}
                  onChange={handleChange}
                  className="w-full rounded border border-gray-200 p-2 text-xs"
                >

                  <option value="">
                    Uncategorized
                  </option>

                  <option value="initial_1on1">
                    Initial 1:1
                  </option>

                  <option value="deep_dive">
                    Deep Dive
                  </option>

                </select>

                <p className="text-[9px] text-gray-500 mt-2">
                  This determines which Initiation milestone can discover bookings from the associated page.
                </p>

              </div>


              {/* Page Name */}

              <div>

                <label className="block text-[10px] font-bold text-gray-600 mb-1">
                  Page Name
                </label>

                <input
                  type="text"
                  name="pageName"
                  value={formData.pageName}
                  onChange={handleChange}
                  placeholder="SlotBoard page name"
                  className="w-full rounded border border-gray-200 p-2"
                />

              </div>


              {/* Public Slug */}

              <div>

                <label className="block text-[10px] font-bold text-gray-600 mb-1">
                  Public Slug
                </label>

                <input
                  type="text"
                  name="publicSlug"
                  value={formData.publicSlug}
                  onChange={handleChange}
                  placeholder="Public slug"
                  className="w-full rounded border border-gray-200 p-2"
                />

                <p className="text-[9px] text-gray-500 mt-1 break-all">
                  Public URL: {window.location.origin}/trinity/slotboard/{formData.publicSlug}
                </p>

              </div>


              {/* Passcode */}

              <div>

                <label className="block text-[10px] font-bold text-gray-600 mb-1">
                  New Passcode (Optional)
                </label>

                <input
                  type="text"
                  name="passcode"
                  value={formData.passcode}
                  onChange={handleChange}
                  placeholder="Leave blank to keep existing passcode"
                  className="w-full rounded border border-gray-200 p-2"
                />

              </div>


              {/* Description */}

              <div>

                <label className="block text-[10px] font-bold text-gray-600 mb-1">
                  Description
                </label>

                <textarea
                  name="pageDescription"
                  value={formData.pageDescription}
                  onChange={handleChange}
                  placeholder="Short description for cohort users"
                  className="w-full rounded border border-gray-200 p-2"
                  rows={4}
                />

              </div>


              {/* Calendar ID */}

              <div>

                <label className="block text-[10px] font-bold text-gray-600 mb-1">
                  Google Calendar ID
                </label>

                <input
                  type="text"
                  name="googleCalendarId"
                  value={formData.googleCalendarId}
                  onChange={handleChange}
                  className="w-full rounded border border-gray-200 p-2"
                />

              </div>


              {/* Google Calendar Status */}

              {isLoadingGoogleCalendarStatus ? (

                <PulseLoader color="#ea580c" size={4} />

              ) : googleCalendarConnected ? (

                <div className="bg-lime-50 border border-lime-200 rounded p-3">

                  <p className="text-xs font-bold text-lime-700">
                    Google Calendar Connected
                  </p>

                  {googleEmail && (
                    <p className="text-[10px] text-lime-700 mt-1">
                      {googleEmail}
                    </p>
                  )}

                </div>

              ) : (

                <button
                  type="button"
                  onClick={handleConnectGoogleCalendar}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded px-3 py-2"
                >
                  Connect Google Calendar
                </button>

              )}


              {/* Active */}

              <label className="flex items-center gap-2 bg-white rounded p-3">

                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />

                <span className="text-xs font-bold text-gray-700">
                  Active SlotBoard Page
                </span>

              </label>


              {/* Timezones */}

              <div className="bg-white rounded p-3">

                <p className="text-[10px] font-bold text-gray-700">
                  Displayed Timezones
                </p>

                <p className="text-[9px] text-gray-500 mt-1 mb-3">
                  Timezone labels available to public users.
                </p>

                {formData.allowedTimezones.map(
                  (item, index) => (

                    <div
                      key={index}
                      className="grid grid-cols-12 gap-2 items-center mb-2"
                    >

                      <input
                        type="text"
                        value={item.label}
                        onChange={e =>
                          handleTimezoneChange(
                            index,
                            "label",
                            e.target.value
                          )
                        }
                        placeholder="Timezone label"
                        className="col-span-4 min-w-0 rounded border border-gray-200 p-2 text-xs"
                      />

                      <input
                        type="text"
                        value={item.timezone}
                        onChange={e =>
                          handleTimezoneChange(
                            index,
                            "timezone",
                            e.target.value
                          )
                        }
                        placeholder="IANA timezone"
                        className="col-span-8 min-w-0 rounded border border-gray-200 p-2 text-xs"
                      />

                    </div>

                  )
                )}

              </div>

            </div>

          )}


          
          {/* Slots Tab */}

          {activeTab === "slots" && (

            <div
              className="
                h-full
                min-h-0
                min-w-0
                overflow-y-auto
                overscroll-contain
                bg-gray-50
                rounded
                p-2
              "
            >

              <EtUserSchedulingSlotManager
                schedulingPageId={id}
              />

            </div>

          )}

        </div>


        {/* Shared Footer */}

        <div
          className="
            shrink-0
            bg-white
            border-t
            border-gray-200
            flex
            flex-wrap
            justify-end
            items-center
            gap-2
            pt-3
            mt-3
          "
        >

          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-100 text-gray-700 rounded px-4 py-2 text-xs font-bold"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded px-4 py-2 text-xs font-bold disabled:opacity-40"
          >
            {isLoading ? "Updating..." : "Update"}
          </button>

        </div>

      </div>

    </div>

  )

}


export default EtUpdateUserSchedulingPage